import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { RequirePermissions } from "@/app/router/guards";
import { PermissionCodes } from "@/shared/auth/permissions";
import { LazyPage } from "@/app/router/LazyPage";

const ApprovalInboxPage = lazy(() =>
  import("@/modules/approvals").then((m) => ({ default: m.ApprovalInboxPage })),
);
const ApprovalPoliciesAdminPage = lazy(() =>
  import("@/modules/approvals").then((m) => ({
    default: m.ApprovalPoliciesAdminPage,
  })),
);
const PlantsListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.PlantsListPage })),
);
const ProductionLinesListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.ProductionLinesListPage })),
);
const AreasListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.AreasListPage })),
);
const UsersListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.UsersListPage })),
);
const UserDetailPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.UserDetailPage })),
);
const RolesListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.RolesListPage })),
);
const AuditListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.AuditListPage })),
);
const EmailLogsListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.EmailLogsListPage })),
);
const NotifyPrefsPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.NotifyPrefsPage })),
);
const OpsNotificationsPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.OpsNotificationsPage })),
);
const UserProfilePage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.UserProfilePage })),
);
const NotifyEventsPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.NotifyEventsPage })),
);
const SysSettingsHubPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.SysSettingsHubPage })),
);
const StatusColorsConfigPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.StatusColorsConfigPage })),
);
const SysAdminHubPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.SysAdminHubPage })),
);
const ShiftsListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.ShiftsListPage })),
);
const CategoriesListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.CategoriesListPage })),
);
const CategoryDetailPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.CategoryDetailPage })),
);
const MobileAppReleasesListPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.MobileAppReleasesListPage })),
);
const MobileAppReleaseDetailPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.MobileAppReleaseDetailPage })),
);
const MfaAdminPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.MfaAdminPage })),
);
const NumberingAdminPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.NumberingAdminPage })),
);
const DefaultsAdminPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.DefaultsAdminPage })),
);
const NavMenuAdminPage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.NavMenuAdminPage })),
);
const OrganizationProfilePage = lazy(() =>
  import("@/modules/sys").then((m) => ({ default: m.OrganizationProfilePage })),
);

export const sysRoutes: RouteObject[] = [
          {
            element: (
              <RequirePermissions
                codes={[
                  PermissionCodes.SysPlantList, PermissionCodes.SysPlantManage,
                  PermissionCodes.SysLineList, PermissionCodes.SysLineManage,
                  PermissionCodes.SysAreaList, PermissionCodes.SysAreaManage,
                  PermissionCodes.SysOrgList, PermissionCodes.SysOrgManage,
                ]}
                match="any"
              />
            ),
            children: [
              {
                path: "sys/org",
                element: (
                  <LazyPage>
                    <PlantsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/org/new",
                element: (
                  <LazyPage>
                    <PlantsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/lines",
                element: (
                  <LazyPage>
                    <ProductionLinesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/lines/new",
                element: (
                  <LazyPage>
                    <ProductionLinesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/areas",
                element: (
                  <LazyPage>
                    <AreasListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/areas/new",
                element: (
                  <LazyPage>
                    <AreasListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/organization",
                element: (
                  <LazyPage>
                    <OrganizationProfilePage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions
                codes={[PermissionCodes.SysUserList, PermissionCodes.SysUserManage]}
                match="any"
              />
            ),
            children: [
              {
                path: "sys/users",
                element: (
                  <LazyPage>
                    <UsersListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/users/new",
                element: (
                  <LazyPage>
                    <UsersListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/users/:userId/edit",
                element: (
                  <LazyPage>
                    <UserDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/users/:userId",
                element: (
                  <LazyPage>
                    <UserDetailPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions
                codes={[PermissionCodes.SysRoleList, PermissionCodes.SysRoleManage]}
                match="any"
              />
            ),
            children: [
              {
                path: "sys/roles",
                element: (
                  <LazyPage>
                    <RolesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/roles/new",
                element: (
                  <LazyPage>
                    <RolesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/roles/:roleId/edit",
                element: (
                  <LazyPage>
                    <RolesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/roles/:roleId",
                element: (
                  <LazyPage>
                    <RolesListPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions
                codes={[PermissionCodes.SysAuditRead, PermissionCodes.SysEmailLogList]}
                match="any"
              />
            ),
            children: [
              {
                path: "sys/audit",
                element: (
                  <LazyPage>
                    <AuditListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/email-logs",
                element: (
                  <LazyPage>
                    <EmailLogsListPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            path: "sys/admin",
            element: (
              <LazyPage>
                <SysAdminHubPage />
              </LazyPage>
            ),
          },
          {
            element: (
              <RequirePermissions
                codes={[
                  PermissionCodes.SysShiftList, PermissionCodes.SysShiftManage,
                  PermissionCodes.SysCategoryList, PermissionCodes.SysCategoryManage,
                ]}
                match="any"
              />
            ),
            children: [
              {
                path: "sys/shifts",
                element: (
                  <LazyPage>
                    <ShiftsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/shifts/new",
                element: (
                  <LazyPage>
                    <ShiftsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/categories",
                element: (
                  <LazyPage>
                    <CategoriesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/categories/new",
                element: (
                  <LazyPage>
                    <CategoriesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/categories/:categoryId/edit",
                element: (
                  <LazyPage>
                    <CategoryDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/categories/:categoryId",
                element: (
                  <LazyPage>
                    <CategoryDetailPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions
                codes={[
                  PermissionCodes.SysMobileAppReleaseList,
                  PermissionCodes.SysMobileAppReleaseManage,
                ]}
                match="any"
              />
            ),
            children: [
              {
                path: "sys/mobile-app-releases",
                element: (
                  <LazyPage>
                    <MobileAppReleasesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/mobile-app-releases/new",
                element: (
                  <LazyPage>
                    <MobileAppReleasesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/mobile-app-releases/:platform/new",
                element: (
                  <LazyPage>
                    <MobileAppReleaseDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/mobile-app-releases/:platform/:releaseId/edit",
                element: (
                  <LazyPage>
                    <MobileAppReleaseDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/mobile-app-releases/:platform",
                element: (
                  <LazyPage>
                    <MobileAppReleaseDetailPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            path: "sys/settings",
            element: (
              <LazyPage>
                <SysSettingsHubPage />
              </LazyPage>
            ),
          },
          {
            path: "sys/status-colors",
            element: (
              <LazyPage>
                <StatusColorsConfigPage />
              </LazyPage>
            ),
          },
          {
            path: "sys/profile",
            element: (
              <LazyPage>
                <UserProfilePage />
              </LazyPage>
            ),
          },
          {
            path: "sys/notifications",
            element: (
              <LazyPage>
                <OpsNotificationsPage />
              </LazyPage>
            ),
          },
          {
            path: "sys/notify-prefs",
            element: (
              <LazyPage>
                <NotifyPrefsPage />
              </LazyPage>
            ),
          },
          {
            element: <RequirePermissions codes={[PermissionCodes.SysNotifyManage]} />,
            children: [
              {
                path: "sys/notify-events",
                element: (
                  <LazyPage>
                    <NotifyEventsPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: <RequirePermissions codes={[PermissionCodes.SysNavManage]} />,
            children: [
              {
                path: "sys/nav-menu",
                element: (
                  <LazyPage>
                    <NavMenuAdminPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: <RequirePermissions codes={[PermissionCodes.ApprovalAct]} />,
            children: [
              {
                path: "sys/approvals",
                element: (
                  <LazyPage>
                    <ApprovalInboxPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions
                codes={[
                  PermissionCodes.SysMfaList, PermissionCodes.SysMfaManage,
                  PermissionCodes.SysApprovalPolicyList, PermissionCodes.SysApprovalPolicyManage,
                ]}
                match="any"
              />
            ),
            children: [
              {
                path: "sys/mfa",
                element: (
                  <LazyPage>
                    <MfaAdminPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/approval-policies",
                element: (
                  <LazyPage>
                    <ApprovalPoliciesAdminPage />
                  </LazyPage>
                ),
              },
              {
                path: "sys/approval-policies/new",
                element: (
                  <LazyPage>
                    <ApprovalPoliciesAdminPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions
                codes={[
                  PermissionCodes.SysNumberingList, PermissionCodes.SysNumberingManage,
                ]}
                match="any"
              />
            ),
            children: [
              {
                path: "sys/numbering",
                element: (
                  <LazyPage>
                    <NumberingAdminPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions
                codes={[
                  PermissionCodes.SysSettingList, PermissionCodes.SysSettingManage,
                ]}
                match="any"
              />
            ),
            children: [
              {
                path: "sys/defaults",
                element: (
                  <LazyPage>
                    <DefaultsAdminPage />
                  </LazyPage>
                ),
              },
            ],
          },
];
